import { useState } from "react";
import { generateAuthHeader } from "../../../commonfunctions/authentication";
import { log } from "../../../commonfunctions/logger";
import { StyledFormField, StyledFormLayout } from "../../../components/styled/styled-components";
import Button, { ButtonType } from "../../../components/Button/Button";
import SectionCard from "../../../components/SectionCard/SectionCard";
import Toast from "../../../components/Toast/Toast";
import { AlertType } from "../../../interfaces/enums";
import { AlertContent } from "../../../interfaces/interfaces";
import Pencil from "../../../assets/pencil";

const passwordRequirements = [
  {
    label: "At least 1 uppercase letter",
    test: (pwd: string) => /[A-Z]/.test(pwd),
  },
  {
    label: "At least 1 lowercase letter",
    test: (pwd: string) => /[a-z]/.test(pwd),
  },
  {
    label: "At least 1 number",
    test: (pwd: string) => /[0-9]/.test(pwd),
  },
];

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [toast, setToast] = useState<AlertContent | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordMeetsRequirements = (pwd: string) => {
    return passwordRequirements.every((requirement) => requirement.test(pwd));
  };

  const passwordsMatch = () => {
    return password !== "" && confirmPassword !== "" && password === confirmPassword;
  };

  const isFormValid = () => {
    return (
      currentPassword.trim() !== "" &&
      password.trim() !== "" &&
      confirmPassword.trim() !== "" &&
      passwordMeetsRequirements(password) &&
      passwordsMatch()
    );
  };

  const changePassword = async () => {
    setToast(null);
    setIsSubmitting(true);

    const params = {
      password: currentPassword,
      new_password: password,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_HALFBACK_ROOT_API}/current_customer/reset_password`,
        {
          method: "POST",
          body: JSON.stringify(params),
          headers: {
            ...generateAuthHeader().headers,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.ok) {
        const data = await res.json();

        if (data.status === "success") {
          setToast({
            message: "Password changed successfully",
            type: AlertType.Success,
          });
          setCurrentPassword("");
          setPassword("");
          setConfirmPassword("");
        } else if (data.status === "failed") {
          setToast({
            message: "Current password is incorrect. Please try again.",
            type: AlertType.Error,
          });
        } else {
          setToast({
            message: "Something went wrong. Please try again later.",
            type: AlertType.Error,
          });
        }
      } else {
        const error = await res.text();
        throw error;
      }
    } catch (error) {
      log(error, "Failed to change password");
      setToast({
        message: "Failed to change password. Please try again later.",
        type: AlertType.Error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionCard title="Change Password" icon={<Pencil width={20} height={20} />}>
      <div className="change-password">
        <StyledFormLayout>
          <form
            autoComplete="off"
            className="password-form"
            onSubmit={(e) => {
              e.preventDefault();
              changePassword();
            }}
          >
            <StyledFormField>
              <label>Current password</label>
              <input
                type="password"
                role="textbox"
                aria-label="current_password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                disabled={isSubmitting}
              />
            </StyledFormField>
            <StyledFormField>
              <label>New password</label>
              <input
                type="password"
                role="textbox"
                aria-label="new_password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={isSubmitting}
              />
              {password && !passwordMeetsRequirements(password) && (
                <div className="password-field-error">Password does not meet all requirements</div>
              )}
            </StyledFormField>
            <StyledFormField>
              <label>Confirm new password</label>
              <input
                type="password"
                role="textbox"
                aria-label="confirm_new_password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={isSubmitting}
              />
              {confirmPassword && !passwordsMatch() && (
                <div className="password-field-error">Passwords do not match</div>
              )}
            </StyledFormField>
            <div className="password-requirements">
              <p>
                <strong>Password requirements:</strong>
              </p>
              <ul>
                {passwordRequirements.map((requirement, index) => {
                  const isMet = password && requirement.test(password);
                  return (
                    <li key={index} className={isMet ? "requirement-met" : ""}>
                      {isMet ? "✓ " : ""}
                      {requirement.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          </form>
          <Button
            type={ButtonType.primary}
            onClick={changePassword}
            disabled={!isFormValid() || isSubmitting}
            isLoading={isSubmitting}
            style={{ width: "100%" }}
          >
            Change Password
          </Button>
        </StyledFormLayout>
      </div>
      <Toast content={toast} onClick={() => setToast(null)} />
    </SectionCard>
  );
};

export default ChangePassword;
